import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Archive, RotateCcw, Trash2, Search, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

/**
 * Het archief van het Greenvision-spoor. Archiveren zet `deleted_at`; elke
 * lijst-query in de app filtert daar al op, dus een gearchiveerd item verdwijnt
 * uit beeld maar blijft bestaan. Dit scherm is de tegenhanger: terugzien en
 * herstellen. Definitief verwijderen kan alleen een super_admin.
 *
 * Kavels (charging_locations) horen bij het Charging-spoor en hebben hun eigen
 * archief op /crm/charging/archief.
 */

type EntiteitKey = 'projects' | 'leads' | 'organizations' | 'contacts';

type Entiteit = {
  key: EntiteitKey;
  label: string;
  /** Kolommen die we nodig hebben om een rij te tonen. */
  select: string;
  /** Titel van een rij. */
  titel: (r: any) => string;
  /** Regel onder de titel; leeg = niets tonen. */
  subtitel: (r: any) => string;
  /** Waar 'Openen' heen gaat na herstel. */
  pad: (r: any) => string;
  /** Query-keys die na herstel vernieuwd moeten worden. */
  invalideer: string[];
};

const ENTITEITEN: Entiteit[] = [
  {
    key: 'projects',
    label: 'Projecten',
    select: 'id, name, project_number, stage, deleted_at, organizations:org_id(name)',
    titel: (r) => r.name ?? 'Naamloos project',
    subtitel: (r) => [r.project_number, r.organizations?.name].filter(Boolean).join(' · '),
    pad: (r) => `/crm/projects/${r.id}`,
    invalideer: ['projects'],
  },
  {
    key: 'leads',
    label: 'Leads',
    select: 'id, summary, source, stage, deleted_at, organizations:org_id(name), contacts:contact_id(first_name, last_name)',
    titel: (r) =>
      r.organizations?.name ??
      [r.contacts?.first_name, r.contacts?.last_name].filter(Boolean).join(' ') ??
      'Naamloze lead',
    subtitel: (r) => [r.source, r.summary].filter(Boolean).join(' · ').slice(0, 120),
    pad: () => '/crm/leads',
    invalideer: ['leads', 'crm-leads'],
  },
  {
    key: 'organizations',
    label: 'Organisaties',
    select: 'id, name, sector, status, deleted_at',
    titel: (r) => r.name ?? 'Naamloze organisatie',
    subtitel: (r) => [r.sector, r.status].filter(Boolean).join(' · '),
    pad: (r) => `/crm/organisaties/${r.id}`,
    invalideer: ['organizations'],
  },
  {
    key: 'contacts',
    label: 'Contacten',
    select: 'id, first_name, last_name, email, deleted_at, organizations:org_id(name)',
    titel: (r) => [r.first_name, r.last_name].filter(Boolean).join(' ') || 'Naamloos contact',
    subtitel: (r) => [r.email, r.organizations?.name].filter(Boolean).join(' · '),
    pad: (r) => `/crm/contacts/${r.id}`,
    invalideer: ['contacts'],
  },
];

export default function CRMArchief() {
  const { hasRole } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [actief, setActief] = useState<EntiteitKey>('projects');
  const [zoek, setZoek] = useState('');

  const magDefinitiefVerwijderen = hasRole('super_admin');

  // Aantallen per tabblad, zodat je ziet waar iets zit zonder te klikken.
  const { data: aantallen = {} as Record<EntiteitKey, number> } = useQuery({
    queryKey: ['archief-aantallen'],
    queryFn: async () => {
      const paren = await Promise.all(
        ENTITEITEN.map(async (e) => {
          const { count } = await supabase
            .from(e.key as any)
            .select('id', { count: 'exact', head: true })
            .not('deleted_at', 'is', null);
          return [e.key, count ?? 0] as const;
        })
      );
      return Object.fromEntries(paren) as Record<EntiteitKey, number>;
    },
  });

  const entiteit = ENTITEITEN.find((e) => e.key === actief)!;

  const { data: rijen = [], isLoading } = useQuery({
    queryKey: ['archief', actief],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(actief as any)
        .select(entiteit.select)
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const gefilterd = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    if (!q) return rijen;
    return rijen.filter((r) =>
      `${entiteit.titel(r)} ${entiteit.subtitel(r)}`.toLowerCase().includes(q)
    );
  }, [rijen, zoek, entiteit]);

  const vernieuw = () => {
    queryClient.invalidateQueries({ queryKey: ['archief', actief] });
    queryClient.invalidateQueries({ queryKey: ['archief-aantallen'] });
    entiteit.invalideer.forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
  };

  const herstel = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from(actief as any)
        .update({ deleted_at: null, updated_at: new Date().toISOString() } as any)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      vernieuw();
      toast({ title: 'Hersteld', description: 'Het item staat weer in de lijst.' });
    },
    onError: (e: any) => {
      toast({ title: 'Herstellen mislukt', description: e?.message, variant: 'destructive' });
    },
  });

  const verwijderDefinitief = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(actief as any).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      vernieuw();
      toast({ title: 'Definitief verwijderd' });
    },
    onError: (e: any) => {
      toast({ title: 'Verwijderen mislukt', description: e?.message, variant: 'destructive' });
    },
  });

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Archive className="h-6 w-6" />
          Archief
        </h1>
        <p className="text-muted-foreground">
          Gearchiveerde items blijven bewaard en zijn hier te herstellen. Kavels staan in
          het <button className="underline underline-offset-2" onClick={() => navigate('/crm/charging/archief')}>Charging-archief</button>.
        </p>
      </div>

      <Tabs value={actief} onValueChange={(v) => { setActief(v as EntiteitKey); setZoek(''); }}>
        <TabsList>
          {ENTITEITEN.map((e) => (
            <TabsTrigger key={e.key} value={e.key} className="gap-2">
              {e.label}
              {aantallen[e.key] > 0 && (
                <Badge variant="secondary" className="ml-1">{aantallen[e.key]}</Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder={`Zoek in gearchiveerde ${entiteit.label.toLowerCase()}...`}
          value={zoek}
          onChange={(e) => setZoek(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground">Laden...</CardContent></Card>
        ) : gefilterd.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Archive className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
              <p className="text-muted-foreground">
                {zoek
                  ? `Geen resultaten voor "${zoek}"`
                  : `Geen gearchiveerde ${entiteit.label.toLowerCase()}`}
              </p>
            </CardContent>
          </Card>
        ) : (
          gefilterd.map((r) => (
            <Card key={r.id}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold truncate">{entiteit.titel(r)}</h3>
                    <Badge variant="outline" className="shrink-0">Gearchiveerd</Badge>
                  </div>
                  {entiteit.subtitel(r) && (
                    <p className="text-sm text-muted-foreground truncate">{entiteit.subtitel(r)}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Gearchiveerd op{' '}
                    {r.deleted_at
                      ? format(new Date(r.deleted_at), 'd MMMM yyyy HH:mm', { locale: nl })
                      : 'onbekend'}
                  </p>
                </div>

                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(entiteit.pad(r))}
                    title="Openen (het item is nog gearchiveerd)"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => herstel.mutate(r.id)}
                    disabled={herstel.isPending}
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Herstel
                  </Button>

                  {magDefinitiefVerwijderen && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" disabled={verwijderDefinitief.isPending}>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Definitief
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Definitief verwijderen</AlertDialogTitle>
                          <AlertDialogDescription>
                            "{entiteit.titel(r)}" wordt onherstelbaar uit de database verwijderd,
                            samen met alles wat eraan hangt. Herstellen is daarna niet meer mogelijk.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuleren</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => verwijderDefinitief.mutate(r.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Definitief verwijderen
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
